import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Star } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

export const ServiceCard = ({ service, index }) => {
  const icons = {
    Passport: () => (
      <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <circle cx="12" cy="10" r="3" />
        <path d="M8 17h8" />
      </svg>
    ),
    Airplane: () => (
      <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
      </svg>
    ),
    CurrencyCircleDollar: () => (
      <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <path d="M16 8.5c-.5-1-1.79-1.5-3.5-1.5h-1c-1.5 0-2.5 1-2.5 2.5s1 2.5 2.5 2.5h2c1.5 0 2.5 1 2.5 2.5s-1 2.5-2.5 2.5h-1c-1.71 0-3-.5-3.5-1.5" />
        <path d="M12 5.5v2M12 16.5v2" />
      </svg>
    ),
    MapTrifold: () => (
      <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 3L3 7v14l6-4 6 4 6-4V3l-6 4-6-4z" />
        <path d="M9 3v14M15 7v14" />
      </svg>
    ),
    GraduationCap: () => (
      <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M22 10l-10-5L2 10l10 5 10-5z" />
        <path d="M6 12v5c0 2 2.5 3 6 3s6-1 6-3v-5" />
        <path d="M22 10v6" />
      </svg>
    ),
    Buildings: () => (
      <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="4" y="8" width="8" height="14" />
        <rect x="12" y="4" width="8" height="18" />
        <path d="M7 12h2M7 16h2M15 8h2M15 12h2M15 16h2" />
      </svg>
    ),
    DeviceMobile: () => (
      <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="6" y="2" width="12" height="20" rx="2" />
        <path d="M10 18h4" />
      </svg>
    ),
    ShieldCheck: () => (
      <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2l8 4v6c0 5.5-3.5 10-8 11-4.5-1-8-5.5-8-11V6l8-4z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  };

  const IconComponent = icons[service.icon] || icons.Passport;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      data-testid={`service-card-${service.id}`}
      className="group bg-white border border-slate-200 p-8 card-hover"
    >
      <div className="text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform duration-300">
        <IconComponent />
      </div>
      <h3 className="text-xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-3">
        {service.title}
      </h3>
      <p className="text-[#4A5568] text-sm leading-relaxed mb-6">
        {service.description}
      </p>
      <Link
        to={`/services#${service.id}`}
        className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-medium hover:gap-3 transition-all"
      >
        Learn More <ArrowRight size={16} />
      </Link>
    </motion.div>
  );
};

export const TeamCard = ({ member, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.15 }}
      data-testid={`team-card-${member.name.toLowerCase().replace(/\s/g, '-')}`}
      className="group text-center"
    >
      <div className="relative overflow-hidden mb-6 img-zoom">
        <img
          src={member.image}
          alt={member.name}
          className="w-full aspect-square object-cover object-top"
        />
        <div className="absolute inset-0 bg-[#0A1F3D]/0 group-hover:bg-[#0A1F3D]/20 transition-colors duration-300" />
      </div>
      <h3 className="text-xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-1">
        {member.name}
      </h3>
      <p className="text-[#D4AF37] text-sm font-medium mb-3">
        {member.designation}
      </p>
      <p className="text-[#4A5568] text-sm leading-relaxed">
        {member.bio}
      </p>
    </motion.div>
  );
};

export const TestimonialCard = ({ testimonial, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      data-testid={`testimonial-card-${testimonial.id}`}
      className="testimonial-card bg-white border border-slate-200 p-8 card-hover"
    >
      <div className="flex gap-1 mb-4">
        {[...Array(testimonial.rating)].map((_, i) => (
          <Star key={i} size={18} weight="fill" className="text-[#D4AF37]" />
        ))}
      </div>
      <p className="text-[#4A5568] leading-relaxed mb-6 relative z-10">
        "{testimonial.text}"
      </p>
      <div className="pt-4 border-t border-slate-100">
        <p className="font-semibold text-[#0A1F3D]">{testimonial.name}</p>
        <p className="text-sm text-[#4A5568]">{testimonial.role || testimonial.location}</p>
        <p className="text-xs text-[#D4AF37] mt-1">{testimonial.company || testimonial.service}</p>
      </div>
    </motion.div>
  );
};

export const CompanyCard = ({ company, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      data-testid={`company-card-${company.id}`}
      className="group bg-white border border-slate-200 overflow-hidden card-hover"
    >
      <div
        className="h-2"
        style={{ backgroundColor: company.color }}
      />
      <div className="p-8">
        <h3 className="text-xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-2">
          {company.name}
        </h3>
        <p className="text-[#D4AF37] text-sm font-medium mb-4">
          {company.tagline}
        </p>
        <p className="text-[#4A5568] text-sm leading-relaxed mb-6">
          {company.description}
        </p>
        <div className="flex flex-wrap gap-2">
          {company.services.slice(0, 4).map((service, i) => (
            <span
              key={i}
              className="px-3 py-1 bg-[#F5F5F5] text-[#4A5568] text-xs"
            >
              {service}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export const VisaCountryCard = ({ country, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
    >
      <Link
        to={`/visa/${country.id}`}
        data-testid={`visa-card-${country.id}`}
        className="block group bg-white border border-slate-200 p-6 card-hover relative"
      >
        <span className="absolute top-4 right-4 text-3xl">{country.flag}</span>
        <h3 className="text-lg font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-2 pr-12">
          {country.name}
        </h3>
        <p className="text-sm text-[#4A5568]">
          Processing: {country.processingTime}
        </p>
        <div className="mt-4 flex items-center gap-2 text-[#D4AF37] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          Apply Now <ArrowRight size={16} />
        </div>
      </Link>
    </motion.div>
  );
};

export const AwardCard = ({ award, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      data-testid={`award-card-${index}`}
      className="bg-white border border-slate-200 overflow-hidden card-hover group"
    >
      <div className="aspect-[4/3] overflow-hidden bg-slate-100">
        <img
          src={award.image_url || award.image}
          alt={award.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-5">
        <h3 className="font-bold font-['Playfair_Display'] text-[#0A1F3D] text-sm leading-tight mb-2 line-clamp-2">
          {award.title}
        </h3>
        <p className="text-[#4A5568] text-xs leading-relaxed mb-3 line-clamp-2">
          {award.caption}
        </p>
        <div className="flex items-center justify-between text-xs">
          <span className="text-[#D4AF37] font-semibold">{award.date}</span>
          {award.venue && <span className="text-[#4A5568] truncate ml-2">{award.venue}</span>}
        </div>
      </div>
    </motion.div>
  );
};

export const CTABanner = () => {
  return (
    <section data-testid="cta-banner" className="bg-[#0A1F3D] py-20 relative grain-overlay">
      <div className="container-custom text-center relative z-10">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Playfair_Display'] text-white mb-6"
        >
          Ready to Start Your Journey?
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-white/70 text-lg mb-8 max-w-2xl mx-auto"
        >
          Let our experts help you plan the perfect trip. Contact us today for personalized travel solutions.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Link to="/contact">
            <Button
              data-testid="cta-contact-btn"
              className="bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] px-8 py-6 text-base font-semibold rounded-none"
            >
              Contact Us
            </Button>
          </Link>
          <Link to="/services">
            <Button
              data-testid="cta-services-btn"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base rounded-none"
            >
              Explore Services
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
